import type { TimeSlots } from '@/types/TimeSlot';

export interface SlackChannel {
  name: string;
  enabled: boolean;
  channel_id: string;
}

export interface EmailRecipient {
  value: string; // The email address
  type: 'email';
}

export interface PhoneRecipient {
  value: string; // The phone number
  type: 'phone';
}

export interface QualificationVariableRecipient {
  name: string; // The human-readable variable name
  uuid?: string; // UUID for backend processing
  type: 'qualification_variable';
}

export type Recipient = EmailRecipient | PhoneRecipient | QualificationVariableRecipient;

export interface EmailNotification {
  enabled: boolean;
  recipients: Recipient[];
}

export interface WhatsAppNotification {
  enabled: boolean;
  recipients: Recipient[];
}

export interface NotificationSettings {
  slackChannel: SlackChannel | null;
  emailNotification: EmailNotification | null;
  whatsappNotification: WhatsAppNotification | null;
}

export interface UserStatus {
  uuid: string;
  name: string;
  color: string;
  chatCount: number;
  language?: string;
  description: string;
  slackChannel: SlackChannel | null;
  emailNotification?: EmailNotification | null;
  whatsappNotification?: WhatsAppNotification | null;
}

export interface PricingPlan {
  name: string;
  monthlyPrice: number;
  extraChatsCost: number;
  monthlyChatsLimit: number;
}

export interface GoogleSession {
  uuid: string;
  email: string;
  isValid: boolean;
}

interface Company {
  uuid: string;
  name: string;
  prodAiAgentId: string;
  numActiveChats: number;
  pricingPlan: PricingPlan;
  connectedToFacebook: boolean;
  googleSessions: GoogleSession[];
  userStatuses: Array<UserStatus>;
  slackWorkspaceId: string | null;
}

export interface WhatsappTemplate {
  id: string;
  name: string;
  status: string;
  message: string;
  category: string;
  language: string;
  components: Array<object>;
}

interface FilterSampleRecord {
  id: string;
  name: string;
  phone: string;
}
export interface TestFiltersResponse {
  count: number;
  message: string;
  sampleRecords: Array<FilterSampleRecord>;
}

interface WhatsappTemplates {
  data: Array<WhatsappTemplate>;
}

export enum DynamicValueType {
  NOW = 'now',
  TODAY = 'today',
  DAYS_AGO = 'days_ago',
  DAYS_FROM_NOW = 'days_from_now',
}

export interface CrmStage {
  id: string;
  name: string;
}

export interface CrmTeam {
  id: string;
  name: string;
}

export interface CrmConnectionDetails {
  stages: CrmStage[];
}

export interface CRMFilter {
  value: any;
  uuid: string;
  name: string;
  field: string;
  operator: string;
  createdAt: string;
  updatedAt: string;
  isDynamic: boolean;
  description?: string;
  scheduledTriggerId: string;
  dynamicValue?: {
    type: DynamicValueType;
    offset?: number;
  };
}

interface FieldOperator {
  type: string;
  label: string;
  description: string;
  requiresValue: boolean;
}

export interface FieldOption {
  id: string;
  value: string;
}

export type CRMFieldType = 'string' | 'number' | 'date' | 'datetime' | 'boolean';
export interface CRMField {
  name: string;
  label: string;
  options: FieldOption[];
  fieldType: CRMFieldType;
  supportedOperators: FieldOperator[];
}

export interface DynamicField {
  uuid: string;
  name: string;
  type: string;
  label: string;
  options?: string[];
  description?: string;
}

export interface ScheduledCRMTrigger {
  uuid: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  aiAgentId: string;
  filters: CRMFilter[];
  lastChecked?: string;
  templateName?: string;
  workingHours: TimeSlots;
  crmConnectionId: string;
  config: Record<string, any>;
}

export const useCompanyStore = defineStore('company', () => {
  const { $sellebrate } = useNuxtApp();

  const fetchCompany = () => $sellebrate<Company>('/company');
  const { state: company } = useAsyncState(fetchCompany(), {
    uuid: '',
    name: '',
    userStatuses: [],
    prodAiAgentId: '',
    numActiveChats: 0,
    googleSessions: [],
    slackWorkspaceId: null,
    connectedToFacebook: false,
    pricingPlan: {
      name: '',
      monthlyPrice: 0,
      extraChatsCost: 0,
      monthlyChatsLimit: 0,
    },
  });

  const crmFields = ref<CRMField[]>([]);

  // OAuth state management
  const isOAuthInProgress = ref(false);

  const updateStatuses = async (statuses: Array<UserStatus>) => {
    const statusesWithUpdatedPriority = statuses.map((status, index) => ({ ...status, priority: index }));

    await $sellebrate('/company/statuses', {
      method: 'PUT',
      body: statusesWithUpdatedPriority,
    });

    if (company.value) {
      company.value = {
        ...company.value,
        userStatuses: [...statusesWithUpdatedPriority],
      };
    }
  };

  const deployAgent = async (agentId: string) => {
    await $sellebrate(`/company/ai_agent/deploy/${agentId}`, {
      method: 'POST',
    });
    // Update the entire company object to ensure reactivity
    company.value = {
      ...company.value,
      prodAiAgentId: agentId,
    };
  };

  const whatsappTemplates = ref<WhatsappTemplates>({ data: [] });
  const isLoadingTemplates = ref(true);

  const fetchWhatsappTemplates = async () => {
    isLoadingTemplates.value = true;
    const response = await $sellebrate<WhatsappTemplates>('/chats/whatsapp/templates');
    whatsappTemplates.value = response;
    isLoadingTemplates.value = false;
  };
  fetchWhatsappTemplates();

  // Ensure company data is fetched when store is accessed
  const refreshCompany = async () => {
    try {
      const response = await fetchCompany();
      company.value = response;
    }
    catch (error) {
      console.error('Failed to fetch company data:', error);
    }
  };

  // Fetch only Google sessions (more efficient than full company refresh)
  const refreshGoogleSessions = async () => {
    try {
      const response = await $sellebrate<GoogleSession[]>('/company/google_sessions');
      if (company.value) {
        // Use immutable update to prevent reactivity issues
        company.value = {
          ...company.value,
          googleSessions: response,
        };
      }
    }
    catch (error) {
      console.error('Failed to fetch Google sessions:', error);
    }
  };

  const searchSlackChannels = async (query: string) => {
    return $sellebrate<Array<SlackChannel>>(`/company/slack/search?query=${query}`);
  };

  const googleOauth2 = async () => {
    const config = useRuntimeConfig();
    window.open(`${config.public.baseURL}/company/google/oauth2`, '_blank', 'width=600,height=700');
  };

  const crmTriggers = ref<ScheduledCRMTrigger[]>([]);
  const isLoadingTriggers = ref(false);
  const connectionDetails = ref<CrmConnectionDetails | null>(null);
  const isLoadingDetails = ref(false);
  const dynamicFields = ref<DynamicField[]>([]);
  const isLoadingFields = ref(false);

  const fetchDynamicFields = async (connectionId: string) => {
    isLoadingFields.value = true;
    try {
      const response = await $sellebrate<DynamicField[]>(
        `/company/crm_connections/${connectionId}/fields`,
      );
      dynamicFields.value = response;
    }
    catch {
      showSnackbar({
        autoClose: true,
        variant: 'error',
        title: 'Failed to fetch dynamic fields',
      });
    }
    finally {
      isLoadingFields.value = false;
    }
  };

  const fetchCrmTriggers = async (connectionId: string) => {
    isLoadingTriggers.value = true;
    try {
      const response = await $sellebrate<ScheduledCRMTrigger[]>(
        `/company/crm_connections/${connectionId}/triggers`,
      );
      crmTriggers.value = response;
    }
    catch {
      showSnackbar({
        autoClose: true,
        variant: 'error',
        title: 'Failed to fetch triggers',
      });
    }
    finally {
      isLoadingTriggers.value = false;
    }
  };

  const createCrmTrigger = async (connectionId: string, trigger: Partial<ScheduledCRMTrigger>) => {
    try {
      await $sellebrate(`/company/crm_connections/${connectionId}/triggers`, {
        body: trigger,
        method: 'POST',
      });
      await fetchCrmTriggers(connectionId);
      showSnackbar({
        autoClose: true,
        variant: 'success',
        title: 'Trigger created successfully',
      });
      return true;
    }
    catch {
      showSnackbar({
        autoClose: true,
        variant: 'error',
        title: 'Failed to create trigger',
      });
      return false;
    }
  };

  const updateCrmTrigger = async (
    connectionId: string,
    triggerId: string,
    trigger: Partial<ScheduledCRMTrigger>,
    fetchTriggers: boolean = true,
  ) => {
    try {
      await $sellebrate(`/company/crm_connections/${connectionId}/triggers/${triggerId}`, {
        method: 'PUT',
        body: trigger,
      });
      if (fetchTriggers) {
        await fetchCrmTriggers(connectionId);
      }
      showSnackbar({
        autoClose: true,
        variant: 'success',
        title: 'Trigger updated successfully',
      });
      return true;
    }
    catch {
      showSnackbar({
        autoClose: true,
        variant: 'error',
        title: 'Failed to update trigger',
      });
      return false;
    }
  };

  const deleteCrmTrigger = async (connectionId: string, triggerId: string) => {
    try {
      await $sellebrate(`/company/crm_connections/${connectionId}/triggers/${triggerId}`, {
        method: 'DELETE',
      });
      await fetchCrmTriggers(connectionId);
      showSnackbar({
        autoClose: true,
        variant: 'success',
        title: 'Trigger deleted successfully',
      });
      return true;
    }
    catch {
      showSnackbar({
        autoClose: true,
        variant: 'error',
        title: 'Failed to delete trigger',
      });
      return false;
    }
  };

  const fetchConnectionDetails = async (connectionId: string) => {
    isLoadingDetails.value = true;
    try {
      const response = await $sellebrate<CrmConnectionDetails>(
        `/company/crm_connections/${connectionId}`,
      );
      connectionDetails.value = response;
    }
    catch {
      showSnackbar({
        autoClose: true,
        variant: 'error',
        title: 'Failed to fetch connection details',
      });
    }
    finally {
      isLoadingDetails.value = false;
    }
  };

  const testFilters = async (connectionId: string, filters: CRMFilter[]) => {
    try {
      const response = await $sellebrate<TestFiltersResponse>(
        `/company/crm_connections/${connectionId}/test_filters`,
        {
          body: filters,
          method: 'POST',
        },
      );
      return response;
    }
    catch {
      showSnackbar({
        autoClose: true,
        variant: 'error',
        title: 'Failed to test filters',
      });
      return null;
    }
  };

  const stages = ref<CrmStage[]>([]);
  const isLoadingStages = ref(false);
  const teams = ref<CrmTeam[]>([]);
  const isLoadingTeams = ref(false);

  const fetchStages = async (connectionId: string) => {
    isLoadingStages.value = true;
    try {
      const response = await $sellebrate<CrmStage[]>(
        `/company/crm_connections/${connectionId}/stages`,
      );
      stages.value = response;
    }
    catch {
      showSnackbar({
        autoClose: true,
        variant: 'error',
        title: 'Failed to fetch stages',
      });
    }
    finally {
      isLoadingStages.value = false;
    }
  };

  const fetchTeams = async (connectionId: string) => {
    isLoadingTeams.value = true;
    try {
      const response = await $sellebrate<CrmTeam[]>(
        `/company/crm_connections/${connectionId}/teams`,
      );
      teams.value = response;
    }
    catch {
      showSnackbar({
        autoClose: true,
        variant: 'error',
        title: 'Failed to fetch teams',
      });
    }
    finally {
      isLoadingTeams.value = false;
    }
  };

  const fetchCRMFields = async (connectionId: string) => {
    crmFields.value = await $sellebrate<CRMField[]>(
      `/company/crm_connections/${connectionId}/fields`,
    );
  };

  return {
    teams,
    stages,
    crmFields,
    fetchTeams,
    deployAgent,
    crmTriggers,
    fetchStages,
    testFilters,
    googleOauth2,
    dynamicFields,
    updateStatuses,
    refreshCompany,
    isLoadingTeams,
    fetchCRMFields,
    isLoadingFields,
    isLoadingStages,
    fetchCrmTriggers,
    createCrmTrigger,
    updateCrmTrigger,
    deleteCrmTrigger,
    isLoadingDetails,
    whatsappTemplates,
    isLoadingTriggers,
    connectionDetails,
    isOAuthInProgress,
    isLoadingTemplates,
    fetchDynamicFields,
    searchSlackChannels,
    refreshGoogleSessions,
    fetchWhatsappTemplates,
    fetchConnectionDetails,
    company: toReactive(company),
  };
});
